import { useState } from "react";
import { useLoaderData } from "react-router";
import { DataSourceNotice } from "@/components/layout/data-source-notice";
import { PageHeader } from "@/components/layout/page-header";
import { Badge, Button, EmptyState, Panel, PanelHeader, type Tone } from "@/components/ui";
import { useDocumentTitle } from "@/lib/document-title";
import { formatDate } from "@/lib/format";
import { loadPaddle } from "@/lib/paddle";
import {
  openPortal,
  readBilling,
  startCheckout,
  type BillingState,
} from "@/services/billing";

export async function billingLoader(): Promise<BillingState> {
  return readBilling();
}

/**
 * Statuses in the language Paddle uses, because that is what the Paddle
 * dashboard says and an operator comparing the two should not have to
 * translate. Only `active` and `trialing` grant access — the backend decides
 * that, and `grantsAccess` carries the decision.
 */
const STATUS_TONE: Record<string, Tone> = {
  active: "positive",
  trialing: "positive",
  past_due: "negative",
  paused: "caution",
  canceled: "neutral",
};

export function BillingPage() {
  useDocumentTitle("Billing");
  const state = useLoaderData<BillingState>();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function buy(plan: string) {
    setBusy(plan);
    setError(null);
    try {
      const checkout = await startCheckout(plan);
      if (!checkout.ok) {
        setError("Could not start checkout. Try again in a moment.");
        return;
      }
      if (!state.clientToken) {
        setError("Billing is not configured for this environment.");
        return;
      }
      const paddle = await loadPaddle();
      // Sandbox has to be selected explicitly; live is Paddle's default.
      if (state.environment === "sandbox") paddle.Environment?.set("sandbox");
      paddle.Initialize({ token: state.clientToken });
      paddle.Checkout.open({ transactionId: checkout.transactionId });
    } catch {
      // The usual cause is an ad blocker eating Paddle's script. Saying so
      // beats a button that does nothing for no visible reason.
      setError("Checkout could not be opened. A browser extension may be blocking it.");
    } finally {
      setBusy(null);
    }
  }

  async function manage() {
    setBusy("portal");
    setError(null);
    const session = await openPortal();
    setBusy(null);
    if (!session.ok) {
      setError("Could not open the billing portal. Try again in a moment.");
      return;
    }
    window.location.assign(session.url);
  }

  const { subscription } = state;

  return (
    <>
      <PageHeader
        title="Billing"
        description="Paddle is the merchant of record. Invoices, cards and cancellation live there."
      />

      <div className="mx-auto max-w-5xl space-y-4 p-6">
        <DataSourceNotice fallback={state.failure} />

        {error ? (
          <div
            role="alert"
            className="rounded-lg border border-negative/20 bg-negative-soft px-4 py-2.5 text-xs text-negative"
          >
            {error}
          </div>
        ) : null}

        <Panel>
          <PanelHeader
            title="Subscription"
            description="What this organization is entitled to right now."
          />
          {subscription ? (
            <dl className="divide-y divide-line text-sm">
              <div className="flex items-center justify-between gap-4 px-4 py-2.5">
                <dt className="text-ink-muted">Status</dt>
                <dd>
                  <Badge tone={STATUS_TONE[subscription.status] ?? "neutral"}>
                    {subscription.status}
                  </Badge>
                </dd>
              </div>
              <div className="flex items-center justify-between gap-4 px-4 py-2.5">
                <dt className="text-ink-muted">Access</dt>
                <dd className="text-ink">
                  {subscription.grantsAccess ? "Granted" : "Not granted"}
                </dd>
              </div>
              {subscription.currentPeriodEnd ? (
                <div className="flex items-center justify-between gap-4 px-4 py-2.5">
                  <dt className="text-ink-muted">Renews</dt>
                  <dd className="numeric text-ink">
                    {formatDate(subscription.currentPeriodEnd)}
                  </dd>
                </div>
              ) : null}
              <div className="flex items-center justify-between gap-4 px-4 py-3">
                <dt className="text-ink-muted">Manage at Paddle</dt>
                <dd>
                  <Button
                    type="button"
                    disabled={busy !== null}
                    onClick={() => void manage()}
                  >
                    {busy === "portal" ? "Opening…" : "Open billing portal"}
                  </Button>
                </dd>
              </div>
            </dl>
          ) : (
            <EmptyState
              title="No subscription"
              detail="Choose a plan below to start one."
            />
          )}
        </Panel>

        <Panel>
          <PanelHeader
            title="Plans"
            description="Paddle states the price, in its own checkout."
          />
          {state.plans.length === 0 ? (
            <EmptyState
              title="Nothing on sale"
              detail="No plans are configured for this environment."
            />
          ) : (
            <ul className="divide-y divide-line">
              {state.plans.map((plan) => (
                <li
                  key={plan}
                  className="flex flex-wrap items-center justify-between gap-4 px-4 py-3"
                >
                  <span className="text-sm font-medium capitalize text-ink">{plan}</span>
                  <Button
                    type="button"
                    variant="primary"
                    disabled={busy !== null}
                    onClick={() => void buy(plan)}
                  >
                    {busy === plan ? "Opening…" : "Choose"}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </>
  );
}
